import { Component, inject, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ViewChild } from '@angular/core';
import { SearchInputComponent } from '../../inputs/search-input/search-input.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../inputs/status-filter/status-filter.component';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StatusComponent } from '../../ui/status/status.component';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { issueText, rowIssues } from '../csv-cleanup-table/csv-cleanup-table.component';
import {
  IContact,
  IValidatedContactBatchLine,
} from '../../../types/contactModule';
import { ContactDetailComponent } from '../../organization/contacts/contact-detail/contact-detail.component';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';
import { EditUploadedContactComponent } from '../../organization/contacts/csv/edit-uploaded-contact/edit-uploaded-contact.component';
import { CsvServiceService } from '../../../services/csv/csv-service.service';
import { CsvAssignMultipleContactsToOrganizationsComponent } from '../csv-assign-multiple-contacts-to-organizations/csv-assign-multiple-contacts-to-organizations.component';
import { CsvAssignMultipleContactsToTagsComponent } from '../csv-assign-multiple-contacts-to-tags/csv-assign-multiple-contacts-to-tags.component';
import { cn } from '../../../../utils/utils';
import { DataTableComponent } from '../../ui/data-table/data-table.component';

type IFilterOptions = 'Successful' | 'Error' | 'All';

@Component({
  selector: 'uploaded-contacts-review-table',
  standalone: true,
  imports: [
    SearchInputComponent,
    StatusFilterComponent,
    MatTooltipModule,
    StatusComponent,
    EmptyStateComponent,
    ContactDetailComponent,
    PromptCardComponent,
    EditUploadedContactComponent,
    CsvAssignMultipleContactsToOrganizationsComponent,
    CsvAssignMultipleContactsToTagsComponent,
    DataTableComponent,
    PaginationComponent,
  ],
  templateUrl: './uploaded-contacts-review-table.component.html',
  styleUrls: [
    '../csv-cleanup-table/csv-import-table.css',
    './uploaded-contacts-review-table.component.css',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadedContactsReviewTableComponent {
  @ViewChild(SearchInputComponent) searchInput?: SearchInputComponent;

  csvService = inject(CsvServiceService);
  private cdr = inject(ChangeDetectorRef);

  filterTitle: IFilterOptions = 'All';
  searchTerm: string = '';

  readonly issueText = issueText;

  readonly filterOptions: StatusFilterOption[] = [
    { value: 'All', title: 'All rows', icon: 'list', tone: 'neutral' },
    { value: 'Successful', title: 'Ready', icon: 'check_circle', tone: 'success' },
    { value: 'Error', title: 'Has problems', icon: 'error', tone: 'danger' },
  ];

  showDeleteSelectedPrompt = false;

  handleAction(action: string) {
    if (this.selectedIndexes.length === 0) return;
    switch (action) {
      case 'assign-organization':
        this.assignSelectedContactsToOrganizations();
        break;
      case 'assign-tags':
        this.openAssignMultipleTagsModal();
        break;
      case 'delete':
        this.showDeleteSelectedPrompt = true;
        this.cdr.markForCheck();
        break;
    }
  }

  closeDeleteSelectedPrompt() {
    this.showDeleteSelectedPrompt = false;
    this.cdr.markForCheck();
  }

  confirmDeleteSelected() {
    this.showDeleteSelectedPrompt = false;
    this.deleteSelectedContacts();
  }

  clearSearchAndFilter() {
    this.searchInput?.writeValue('');
    this.filterTitle = 'All';
    this.searchTerm = '';
    this.currentPage = 0;
    this._invalidateCache();
    this.cdr.markForCheck();
  }

  rowKey(line: IValidatedContactBatchLine, index: number): string {
    return String(line.personEntity?.id ?? `i-${index}`);
  }

  rowNumber(line: IValidatedContactBatchLine): number | null {
    return this.csvService.rowNumber(line);
  }

  name(line: IValidatedContactBatchLine | null): string {
    const p = line?.personEntity;
    return `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim() || 'No name';
  }

  issues(line: IValidatedContactBatchLine) {
    return rowIssues(line);
  }

  groupNames(items: { name?: string | null }[] | undefined): string {
    const names = (items ?? []).map((item) => item?.name).filter(Boolean) as string[];
    if (!names.length) return '—';
    return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
  }

  groupTitle(items: { name?: string | null }[] | undefined): string | null {
    const names = (items ?? []).map((item) => item?.name).filter(Boolean) as string[];
    return names.length > 1 ? names.join(', ') : null;
  }

  get selectionLabel(): string {
    const count = this.selectedIndexes.length;
    return count === 1 ? '1 row selected' : `${count} rows selected`;
  }

  get someSelected(): boolean {
    return this.selectedIndexes.length > 0 && !this.getIsAllSelected;
  }

  /** Enter on the row opens it; Enter on a row button stays with the button. */
  openFromKeyboard(event: Event, contact: IValidatedContactBatchLine) {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.openContactDetail(contact, contact.personEntity.id!);
  }

  // Cache filtered contacts to avoid recalculating on every change detection
  private _cachedFilteredContacts: IValidatedContactBatchLine[] = [];
  private _lastFilterTitle: IFilterOptions = 'All';
  private _lastSearchTerm: string = '';
  private _lastContactsLength: number = 0;
  private _lastVersion: number = -1;

  merge = cn;

  closeFilterOptions() {}

  // Pagination — render only the current page's rows. Rendering all rows at once
  // (each with a dropdown + icons) is what froze the UI on large imports.
  pageSize = 50;
  currentPage = 0;

  updateFilterOption(option: IFilterOptions) {
    this.filterTitle = option || 'All';
    this.currentPage = 0;
    this._invalidateCache();
    this.cdr.markForCheck();
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    this.currentPage = 0;
    this._invalidateCache();
    this.cdr.markForCheck();
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.getFilteredContacts.length / this.pageSize)
    );
  }

  get pagedContacts(): IValidatedContactBatchLine[] {
    const list = this.getFilteredContacts;
    const lastPage = Math.max(0, Math.ceil(list.length / this.pageSize) - 1);
    const page = Math.min(Math.max(0, this.currentPage), lastPage);
    const start = page * this.pageSize;
    return list.slice(start, start + this.pageSize);
  }

  goToPage(page: number) {
    this.currentPage = page;
    this.cdr.markForCheck();
  }

  nextPage() {
    this.currentPage += 1;
    this.cdr.markForCheck();
  }

  prevPage() {
    if (this.currentPage > 0) this.currentPage -= 1;
    this.cdr.markForCheck();
  }

  changePageSize(size: number) {
    this.pageSize = size;
    this.currentPage = 0;
    this.cdr.markForCheck();
  }

  private clampCurrentPage() {
    const lastPage = Math.max(
      0,
      Math.ceil(this.getFilteredContacts.length / this.pageSize) - 1
    );
    if (this.currentPage > lastPage) this.currentPage = lastPage;
  }

  private _invalidateCache(): void {
    this._cachedFilteredContacts = [];
  }

  private _computeFilteredContacts(): IValidatedContactBatchLine[] {
    const contacts = this.csvService.getValidatedContacts;

    // Apply status filter first
    let filteredContacts: IValidatedContactBatchLine[];

    switch (this.filterTitle) {
      case 'Successful':
        filteredContacts = contacts.filter(
          (el) => el.batchLineValidationStatus === 'PROCESSED_OK'
        );
        break;
      case 'Error':
        filteredContacts = contacts.filter(
          (el) => el.batchLineValidationStatus === 'PROCESSING_ERROR'
        );
        break;
      case 'All':
      default:
        filteredContacts = contacts;
    }

    // Apply search filter if search term exists
    const trimmedSearch = this.searchTerm.trim();
    if (trimmedSearch) {
      const searchLower = trimmedSearch.toLowerCase();

      filteredContacts = filteredContacts.filter((contact) => {
        const entity = contact.personEntity;
        const firstName = entity.firstName?.toLowerCase() || '';
        const lastName = entity.lastName?.toLowerCase() || '';

        // Early exit optimization - check most common searches first
        if (firstName.includes(searchLower) || lastName.includes(searchLower)) {
          return true;
        }

        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName.includes(searchLower)) {
          return true;
        }

        const email = entity.emailAddress?.toLowerCase() || '';
        if (email.includes(searchLower)) {
          return true;
        }

        const phone = entity.primaryContactNumber?.toLowerCase() || '';
        if (phone.includes(searchLower)) {
          return true;
        }

        const gender = entity.gender?.toLowerCase() || '';
        return gender.includes(searchLower);
      });
    }

    return filteredContacts;
  }

  get getFilteredContacts(): IValidatedContactBatchLine[] {
    const contacts = this.csvService.getValidatedContacts;
    const currentContactsLength = contacts.length;

    // Check if cache is valid
    const cacheValid =
      this._cachedFilteredContacts.length > 0 &&
      this._lastFilterTitle === this.filterTitle &&
      this._lastSearchTerm === this.searchTerm &&
      this._lastContactsLength === currentContactsLength &&
      this._lastVersion === this.csvService.version;

    if (cacheValid) {
      return this._cachedFilteredContacts;
    }

    // Recompute and cache
    this._cachedFilteredContacts = this._computeFilteredContacts();
    this._lastFilterTitle = this.filterTitle;
    this._lastSearchTerm = this.searchTerm;
    this._lastContactsLength = currentContactsLength;
    this._lastVersion = this.csvService.version;

    return this._cachedFilteredContacts;
  }

  // Edit Contact stuff
  openEditContactModal: boolean = false;
  selectedContact: IValidatedContactBatchLine | null = null;
  selectedContactIndex: (string | number) | null = null;
  showContactDetail: boolean = false;
  showDeletePrompt: boolean = false;

  // assign organizations and tags
  showAssignMultipleOrganizationsModal: boolean = false;
  showAssignMultipleTagsModal: boolean = false;

  openAssignMultipleOrganizationsModal() {
    this.showAssignMultipleOrganizationsModal = true;
  }
  closeAssignMultipleOrganizationsModal() {
    this.showAssignMultipleOrganizationsModal = false;
  }
  openAssignMultipleTagsModal() {
    this.showAssignMultipleTagsModal = true;
  }
  closeAssignMultipleTagsModal() {
    this.showAssignMultipleTagsModal = false;
  }

  selectedIndexes: (string | number)[] = [];
  private _selectedIndexesSet = new Set<string | number>();

  selectIndex(index: string | number) {
    if (this._selectedIndexesSet.has(index)) {
      this._selectedIndexesSet.delete(index);
      this.selectedIndexes = this.selectedIndexes.filter((i) => i !== index);
    } else {
      this._selectedIndexesSet.add(index);
      this.selectedIndexes.push(index);
    }
    this.cdr.markForCheck();
  }

  selectAllIndexes() {
    const filteredContacts = this.getFilteredContacts;
    this.selectedIndexes = filteredContacts.map(
      (contact) => contact.personEntity.id!
    );
    this._selectedIndexesSet = new Set(this.selectedIndexes);
    this.cdr.markForCheck();
  }

  deselectAllIndexes() {
    this.selectedIndexes = [];
    this._selectedIndexesSet.clear();
    this.cdr.markForCheck();
  }

  toggleSelectAllIndexes() {
    const filteredLength = this.getFilteredContacts.length;
    if (this.selectedIndexes.length === filteredLength && filteredLength > 0) {
      this.deselectAllIndexes();
    } else {
      this.selectAllIndexes();
    }
  }

  get getIsAllSelected(): boolean {
    const filteredLength = this.getFilteredContacts.length;
    return filteredLength > 0 && this.selectedIndexes.length === filteredLength;
  }

  getIndexIsSelected(index: string | number): boolean {
    return this._selectedIndexesSet.has(index);
  }

  updateSelectedContact(contact: IValidatedContactBatchLine, index: string | number) {
    this.selectedContact = contact;
    this.selectedContactIndex = index;
  }

  updateSelectedContactIndex(index: string | number) {
    this.selectedContactIndex = index;
  }

  openContactDetail(contact: IValidatedContactBatchLine, index: string | number) {
    this.updateSelectedContact(contact, index);
    this.showContactDetail = true;
    this.cdr.markForCheck();
  }

  closeContactDetail() {
    this.showContactDetail = false;
    this.cdr.markForCheck();
  }

  openEditContact(contact: IValidatedContactBatchLine, index: string | number) {
    this.updateSelectedContact(contact, index);
    this.openEditContactModal = true;
    this.cdr.markForCheck();
  }

  closeEditContact() {
    this.openEditContactModal = false;
    this._invalidateCache();
    this.cdr.markForCheck();
  }

  openDeletePrompt(contact: IValidatedContactBatchLine, index: string | number) {
    this.updateSelectedContact(contact, index);
    this.showDeletePrompt = true;
    this.cdr.markForCheck();
  }

  closeDeletePrompt() {
    this.showDeletePrompt = false;
    this.cdr.markForCheck();
  }

  // delete the contact from the upload contact list
  deleteContact() {
    if (this.selectedContactIndex !== null) {
      this.csvService.deleteValidatedContact(this.selectedContactIndex);

      // Remove from selected indexes if present
      if (this._selectedIndexesSet.has(this.selectedContactIndex)) {
        this._selectedIndexesSet.delete(this.selectedContactIndex);
        this.selectedIndexes = this.selectedIndexes.filter(
          (i) => i !== this.selectedContactIndex
        );
      }

      this._invalidateCache();
      this.clampCurrentPage();
      this.closeDeletePrompt();
    }
  }

  deleteSelectedContacts() {
    if (this.selectedIndexes.length === 0) {
      return;
    }

    this.csvService.deleteBatchOfContacts(this.selectedIndexes);
    this.selectedIndexes = [];
    this._selectedIndexesSet.clear();
    this._invalidateCache();
    this.clampCurrentPage();
    this.closeFilterOptions();
    this.cdr.markForCheck();
  }

  assignSelectedContactsToOrganizations() {
    this.openAssignMultipleOrganizationsModal();
    this.cdr.markForCheck();
  }

  assignSelectedContactsToTag() {
    this.openAssignMultipleOrganizationsModal();
    this.cdr.markForCheck();
  }

  resetSelectedContactIndexes() {
    this.selectedIndexes = [];
    this._selectedIndexesSet.clear();
    this.cdr.markForCheck();
  }

  ngOnInit() {
    // Initialize cache with current contacts
    this._invalidateCache();

    // Pre-compute filtered contacts on init to avoid initial lag
    if (this.csvService.getValidatedContacts.length > 0) {
      this.getFilteredContacts;
    }
  }

  ngOnDestroy() {
    // Clean up
    this._cachedFilteredContacts = [];
    this._selectedIndexesSet.clear();
  }
}
